import React, { useState } from 'react'
import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import { useParams } from 'utils/hooks'
import Box from 'components_new/atoms/Box'
import TextField from 'components_new/atoms/TextField'
import Button from 'components_new/atoms/Button'
import Text from 'components_new/atoms/Text'
import Logotype from 'assets/logotypes/Logotype'
import {
  validatePassword,
  postCredentials
} from 'redux/api/PublicCredentialsGroups'
import {
  GlobalPublicCredentialsGroup,
  GlobalPublicCredential
} from 'types/GlobalCredentials'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

const PublicSetCredentials = () => {
  const { uniqueString } = useParams()
  const theme = getTheme('dark', null, { responsive: true })

  const [credentialsGroup, setCredentialsGroup] =
    useState<GlobalPublicCredentialsGroup | null>(null)
  const [password, setPassword] = useState<string>('')
  const [postDone, setPostDone] = useState<boolean>(false)

  const getChildren = () => {
    if (postDone) {
      return <ThankYouContent />
    }

    if (credentialsGroup?.values_set) {
      return <ValuesSetContent />
    }

    if (credentialsGroup) {
      const sortedCredentials = [...credentialsGroup.credentials].sort((a, b) =>
        sortAlphabeticalAsc(a.label, b.label)
      )

      return (
        <SetCredentialsContent
          credentials={sortedCredentials}
          uniqueString={uniqueString}
          password={password}
          setPostDone={setPostDone}
        />
      )
    }

    return (
      <PasswordContent
        password={password}
        setPassword={setPassword}
        setCredentialsGroup={setCredentialsGroup}
        uniqueString={uniqueString}
      />
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default'
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <a href="https://homepal.se">
            <Box
              sx={{
                position: 'absolute',
                top: 24,
                left: 24,
                height: 32,
                width: 120,
                color: 'white.main'
              }}
            >
              <Logotype />
            </Box>
          </a>
          <Box sx={{ maxWidth: 600, p: 2 }}>{getChildren()}</Box>
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bottom: 24
            }}
          >
            <Text align="center" color="text.secondary" variant="body2">
              Copyright © Homepal AB 2025.
              <br />
              Org. nr: 559201-1521 (SE).
            </Text>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}

const PasswordContent = (props: {
  password: string
  setPassword: (val: string) => void
  setCredentialsGroup: (val: GlobalPublicCredentialsGroup) => void
  uniqueString: string
}) => {
  const [wrongPassword, setWrongPassword] = useState<boolean>(false)

  return (
    <Box sx={{ width: 400, display: 'flex', flexDirection: 'column' }}>
      <Text
        gutterBottom={true}
        variant="h4"
        align="center"
        sx={{
          fontFamily: 'DM Sans',
          lineHeight: 1.1,
          letterSpacing: '-0.05em'
        }}
      >
        Ange lösenord för att lämna uppgifter
      </Text>
      <TextField
        label={'Lösenord'}
        required={true}
        error={wrongPassword}
        helperText={wrongPassword ? 'Fel lösenord, försök igen.' : ''}
        autoComplete="off"
        value={props.password}
        type={'password'}
        onChange={(event) => props.setPassword(event.target.value)}
        sx={{ mt: 0, mb: 0 }}
      />
      <Button
        disabled={props.password.trim() === ''}
        variant="contained"
        sx={{ mt: 2 }}
        color="brand"
        onClick={() =>
          validatePassword({
            unique_string: props.uniqueString,
            password: props.password
          })
            .then((group) => props.setCredentialsGroup(group.data.data))
            .catch(() => setWrongPassword(true))
        }
      >
        Gå vidare
      </Button>
    </Box>
  )
}

const SetCredentialsContent = (props: {
  credentials: GlobalPublicCredential[]
  uniqueString: string
  password: string
  setPostDone: (val: boolean) => void
}) => {
  const { credentials, uniqueString, password, setPostDone } = props
  const [values, setValues] = useState<{ id: string; value: string }[]>(
    credentials.map((cred) => ({
      id: cred.id,
      value: ''
    }))
  )

  return (
    <Box sx={{ width: 400, display: 'flex', flexDirection: 'column' }}>
      <Text
        gutterBottom={true}
        variant="h4"
        align="center"
        sx={{
          fontFamily: 'DM Sans',
          lineHeight: 1.1,
          letterSpacing: '-0.05em'
        }}
      >
        Ange uppgifter nedan
      </Text>
      {credentials.map((credential, i) => (
        <TextField
          key={credential.id}
          label={credential.label}
          required={true}
          autoComplete="off"
          value={values[i].value}
          onChange={(event) =>
            setValues((prev) => {
              const newValues = [...prev]

              newValues[i] = { ...newValues[i], value: event.target.value }

              return newValues
            })
          }
          sx={{ mt: 0, mb: 1 }}
        />
      ))}

      <Button
        variant="contained"
        disabled={values.some((val) => val.value.trim() === '')}
        sx={{ mt: 1 }}
        color="brand"
        onClick={() =>
          postCredentials({
            unique_string: uniqueString,
            password: password,
            credentials: values
          }).then(() => setPostDone(true))
        }
      >
        Skicka in
      </Button>
    </Box>
  )
}

const ValuesSetContent = () => {
  return (
    <Box sx={{ width: 400, display: 'flex', flexDirection: 'column' }}>
      <Text
        gutterBottom={true}
        variant="h4"
        align="center"
        sx={{
          fontFamily: 'DM Sans',
          lineHeight: 1.1,
          letterSpacing: '-0.05em'
        }}
      >
        Uppgifter har redan tagits emot!
      </Text>
    </Box>
  )
}

const ThankYouContent = () => {
  return (
    <Box sx={{ width: 400, display: 'flex', flexDirection: 'column' }}>
      <Text
        gutterBottom={true}
        variant="h4"
        align="center"
        sx={{
          fontFamily: 'DM Sans',
          lineHeight: 1.1,
          letterSpacing: '-0.05em'
        }}
      >
        Tack för uppgifterna!
      </Text>
    </Box>
  )
}

export default PublicSetCredentials
